import { useMutation, useQuery } from "convex/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../convex/_generated/api";
import type { Id } from "../convex/_generated/dataModel";
import { DropZone } from "./components/DropZone";
import { PlayerBar } from "./components/PlayerBar";
import { Reader } from "./components/Reader";
import { Sidebar } from "./components/Sidebar";
import type { Sentence } from "./lib/pdf";
import { useSpeech, useVoices } from "./lib/tts";

type FolderFilter = "all" | "unfiled" | Id<"folders">;

export default function App() {
  const docsResult = useQuery(api.documents.list);
  const docs = docsResult ?? [];
  const folders = useQuery(api.folders.list) ?? [];
  const settings = useQuery(api.settings.get);
  const [activeDocId, setActiveDocId] = useState<Id<"documents"> | null>(null);
  const activeDetail = useQuery(
    api.documents.get,
    activeDocId ? { id: activeDocId } : "skip",
  );
  const createFolder = useMutation(api.folders.create);
  const createDocument = useMutation(api.documents.create);
  const removeDocument = useMutation(api.documents.remove);
  const assignFolder = useMutation(api.documents.assignFolder);
  const setPosition = useMutation(api.documents.setPosition);
  const patchSettings = useMutation(api.settings.patch);
  const generateUploadUrl = useMutation(api.documents.generateUploadUrl);

  const [filter, setFilter] = useState<FolderFilter>("all");
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [rate, setRate] = useState(1);
  const [voiceURI, setVoiceURI] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  const voices = useVoices();
  const { speak, stop } = useSpeech();

  useEffect(() => {
    if (!settings || docsResult === undefined || hydrated) return;
    setRate(settings.rate);
    setVoiceURI(settings.voiceURI ?? null);
    if (settings.activeDocId && docs.some((doc) => doc._id === settings.activeDocId)) {
      setActiveDocId(settings.activeDocId);
      const match = docs.find((doc) => doc._id === settings.activeDocId);
      setIndex(match?.lastSentenceIndex ?? 0);
    } else if (docs.length === 0) {
      setShowUpload(true);
    }
    setHydrated(true);
  }, [settings, docs, docsResult, hydrated]);

  const activeDoc = activeDetail ?? null;
  const voice = useMemo(
    () => voices.find((item) => item.voiceURI === voiceURI) ?? null,
    [voices, voiceURI],
  );

  useEffect(() => {
    if (!voiceURI && voices.length) {
      const lang = navigator.language?.toLowerCase() ?? "en";
      const match =
        voices.find((item) => item.lang?.toLowerCase() === lang) ??
        voices.find((item) => item.lang?.toLowerCase().startsWith(lang.split("-")[0])) ??
        voices[0];
      setVoiceURI(match?.voiceURI ?? null);
    }
  }, [voices, voiceURI]);

  useEffect(() => {
    if (!playing || !activeDoc) return;
    const sentence = activeDoc.sentences[index];
    if (!sentence) {
      setPlaying(false);
      return;
    }
    speak({
      text: sentence.text,
      rate,
      voice,
      onEnd: () =>
        setIndex((current) =>
          activeDoc.sentences[current + 1] ? current + 1 : current,
        ),
      onError: () => setPlaying(false),
    });
    return () => stop();
  }, [playing, index, activeDocId, rate, voiceURI, activeDoc, speak, stop, voice]);

  useEffect(() => {
    stop();
    setPlaying(false);
  }, [activeDocId, stop]);

  useEffect(() => {
    if (!hydrated) return;
    void patchSettings({
      rate,
      voiceURI: voiceURI ?? undefined,
      activeDocId: activeDocId ?? undefined,
    });
  }, [hydrated, rate, voiceURI, activeDocId, patchSettings]);

  useEffect(() => {
    if (!hydrated || !activeDocId) return;
    void setPosition({ id: activeDocId, lastSentenceIndex: index });
  }, [hydrated, activeDocId, index, setPosition]);

  const onDocReady = useCallback(
    async (payload: {
      title: string;
      folderId: Id<"folders"> | null;
      fullText: string;
      sentences: Sentence[];
      file: File;
    }) => {
      let pdfStorageId: Id<"_storage"> | undefined;
      try {
        const uploadUrl = await generateUploadUrl();
        const response = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": payload.file.type || "application/pdf" },
          body: payload.file,
        });
        if (response.ok) {
          const json = (await response.json()) as { storageId?: Id<"_storage"> };
          pdfStorageId = json.storageId;
        }
      } catch {
        pdfStorageId = undefined;
      }
      const id = await createDocument({
        title: payload.title,
        folderId: payload.folderId ?? undefined,
        fullText: payload.fullText,
        sentences: payload.sentences,
        pdfStorageId,
      });
      setActiveDocId(id);
      setIndex(0);
      setShowUpload(false);
    },
    [createDocument, generateUploadUrl],
  );

  const seek = useCallback(
    (next: number) => {
      if (!activeDoc) return;
      const clamped = Math.max(0, Math.min(next, activeDoc.sentences.length - 1));
      stop();
      setIndex(clamped);
      if (playing) {
        setPlaying(false);
        window.setTimeout(() => setPlaying(true), 30);
      }
    },
    [activeDoc, playing, stop],
  );

  return (
    <div className="flex min-h-screen">
      <Sidebar
        docs={docs}
        folders={folders}
        activeDocId={activeDocId}
        activeFolderFilter={filter}
        onFilterChange={setFilter}
        onSelectDoc={(id) => {
          setActiveDocId(id);
          setShowUpload(false);
          const match = docs.find((doc) => doc._id === id);
          setIndex(match?.lastSentenceIndex ?? 0);
        }}
        onDeleteDoc={(id) => {
          void removeDocument({ id });
          if (activeDocId === id) {
            setActiveDocId(null);
            setPlaying(false);
            stop();
            setShowUpload(true);
          }
        }}
        onCreateFolder={(name) => {
          void createFolder({ name });
        }}
        onAssignFolder={(id, folderId) => {
          void assignFolder({ id, folderId: folderId ?? undefined });
        }}
        onNewUpload={() => {
          setActiveDocId(null);
          setShowUpload(true);
        }}
      />
      <main className="relative flex-1">
        {showUpload || !activeDocId ? (
          <div className="flex min-h-screen items-center justify-center px-6">
            <DropZone
              onDocReady={(doc) => {
                void onDocReady(doc);
              }}
              folderId={
                filter !== "all" && filter !== "unfiled" ? filter : null
              }
            />
          </div>
        ) : !activeDoc ? (
          <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
            Reading your document…
          </div>
        ) : (
          <>
            <Reader
              title={activeDoc.title}
              sentences={activeDoc.sentences}
              activeIndex={index}
              onSeek={seek}
            />
            <PlayerBar
              playing={playing}
              onPlayPause={() => {
                if (!activeDoc) return;
                setPlaying((current) => {
                  if (current) stop();
                  return !current;
                });
              }}
              onSkip={(delta) => seek(index + delta)}
              index={index}
              total={activeDoc.sentences.length}
              onSeek={seek}
              rate={rate}
              onRateChange={(next) => {
                setRate(next);
                if (playing) {
                  stop();
                  setPlaying(false);
                  window.setTimeout(() => setPlaying(true), 30);
                }
              }}
              voices={voices}
              voiceURI={voiceURI}
              onVoiceChange={(uri) => {
                setVoiceURI(uri);
                if (playing) {
                  stop();
                  setPlaying(false);
                  window.setTimeout(() => setPlaying(true), 30);
                }
              }}
              title={activeDoc.title}
            />
          </>
        )}
      </main>
    </div>
  );
}
